import { queryOptions, useQuery } from '@tanstack/react-query';

import { env } from '@/constants/env';
import { i18n } from '@/lib/i18n';

const { MAP_BOX_API_TOKEN } = env;

interface SuggestionResponse {
  id: string;
  type: string;
  address?: string;
  place_type: string[];
  relevance: number;
  properties: {
    wikidata: string;
    category: string;
    landmark: boolean;
    address: string;
    foursquare: string;
    maki: string;
    short_code?: string;
  };
  text: string;
  language: string;
  place_name: string;
  matching_text: string;
  matching_place_name: string;
  center: [number, number];
  geometry: { type: string; coordinates: [number, number] };
  context: {
    id: string;
    mapbox_id: string;
    wikidata?: string;
    short_code?: string;
    text: string;
    language?: string;
  }[];
}

interface Response {
  features: SuggestionResponse[];
}

export interface SuggestionMapped extends SuggestionResponse {
  city?: string;
  houseNumber?: string;
  street?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  countryId?: string;
  postalCode?: string;
  countryCode?: string;
}

const mapFeatureToSuggestion = (feature: SuggestionResponse): SuggestionMapped => {
  const postalCode = feature.context?.find((item) => item.id.startsWith('postcode'))?.text;

  const countryContext = feature.context?.find((item) => item.id.startsWith('country'));
  const cityContext = feature.context?.find((item) => item.id.startsWith('place'));

  const city = feature.place_type.includes('place') ? feature.text : cityContext?.text;
  const countryId = feature.place_type.includes('country')
    ? feature.properties?.short_code?.toUpperCase()
    : countryContext?.short_code?.toUpperCase();

  const [longitude, latitude] = feature.center;
  const address = feature.place_name;
  const street = feature.place_type.includes('address') ? feature.text : undefined;
  const houseNumber = feature.place_type.includes('address') ? feature.address : undefined;

  return {
    ...feature,
    houseNumber,
    street,
    city,
    address,
    countryId,
    postalCode,
    latitude,
    longitude,
  };
};

export const getPlaceSuggestions = async (query: string): Promise<SuggestionMapped[]> => {
  if (query.trim().length === 0) {
    return [];
  }

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
    query
  )}.json?access_token=${MAP_BOX_API_TOKEN}&language=${i18n.language}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Mapbox API error: ${response.status} ${response.statusText}`);
  }

  const data: Response = await response.json();

  return (data.features || []).map(mapFeatureToSuggestion);
};

export const getPlaceSuggestionsQueryOptions = (query: string) => {
  return queryOptions({
    queryKey: ['PLACE_SUGGESTIONS', query],
    queryFn: () => getPlaceSuggestions(query),
  });
};

export const usePlaceSuggestions = (query: string) => {
  return useQuery({ ...getPlaceSuggestionsQueryOptions(query) });
};
